import { FC, ReactNode, useMemo } from 'react';
import Text from '../text';
import { createUseStyles } from 'react-jss';
import { alpha, Stack, Theme } from '@mui/material';
import Icon from '../icon';
import { computeIconSize, EntityLinkSize, EntityLinkVariant } from './helpers';
import classNames from 'classnames';
import StatusChip, { ChipStatus } from '../chip/status-chip';

interface Props {
  image?: ReactNode;
  title: string;
  titleAdornment?: ReactNode;
  subtext?: ReactNode;
  onClick?: () => void;
  size?: EntityLinkSize;
  variant?: EntityLinkVariant;
  iconSize?: number;
  status?: string;
  condensed?: boolean;
}

const useStyles = createUseStyles<
  string,
  {
    variant: EntityLinkVariant;
    iconSize: number;
    hasClick: boolean;
    condensed: boolean;
  }
>((theme: Theme) => ({
  entityLink: {
    padding: ({ variant, condensed }) =>
      variant === 'outlined' ? (condensed ? 4 : '8px 12px') : 2,
    borderRadius: theme.shape.borderRadius,
    borderStyle: ({ variant }) => (variant === 'outlined' ? 'solid' : 'none'),
    borderColor: ({ variant }) => (variant === 'outlined' ? theme.palette.divider : 'transparent'),
    borderWidth: ({ variant }) => (variant === 'outlined' ? 1 : 0),
    backgroundColor: ({ variant }) =>
      variant === 'outlined' ? alpha(theme.palette.common.white, 0.5) : 'inherit',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: 0,
    cursor: ({ hasClick }) => (hasClick ? 'pointer' : 'default'),
    '&:hover': {
      '& $arrow': {
        color: theme.palette.link.hover,
        opacity: 1,
      },
      '& $titleContent': {
        color: theme.palette.link.hover,
      },
    },
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    gap: ({ iconSize }) => (iconSize <= 20 ? 4 : 8),
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
  },
  titleContent: {
    color: theme.palette.link.main,
  },
  arrow: {
    cursor: 'pointer',
    marginLeft: theme.spacing(1),
    color: theme.palette.gray,
    opacity: ({ variant }) => (variant === 'outlined' ? 1 : 0),
    transition: 'opacity 0.2s ease',
  },
  image: {
    width: ({ iconSize }) => iconSize,
    height: ({ iconSize }) => iconSize,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    '& > img': {
      width: ({ iconSize }) => iconSize,
      height: ({ iconSize }) => iconSize,
    },
  },
}));

export const EntityLink: FC<Props> = ({
  image,
  title,
  titleAdornment,
  subtext,
  onClick,
  size = 'small',
  variant = 'outlined',
  iconSize,
  status,
  condensed = false,
}) => {
  const computedIconSize = useMemo(
    () => iconSize ?? computeIconSize(size, !!subtext),
    [iconSize, size, subtext]
  );

  const hasClick = !!onClick;
  const styles = useStyles({ hasClick, variant, iconSize: computedIconSize, condensed });

  const showStatus =
    !!status &&
    ['error', 'failed', 'inactive', 'pending', 'deprecated', 'degraded'].includes(status);

  const compTitleClass = classNames(hasClick && styles.titleContent);

  const ContentElement = (
    <Stack className={styles.content}>
      {!!image && <Stack className={styles.image}>{image}</Stack>}
      <Stack minWidth={0} flex={1}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Text size={size} dotdot className={compTitleClass}>
            {title}
          </Text>
          {titleAdornment}
          {showStatus && <StatusChip value={status as ChipStatus} size="x-small" />}
        </Stack>
        {!!subtext && (
          <Text size="small" color="grey" dotdot>
            {subtext}
          </Text>
        )}
      </Stack>
    </Stack>
  );

  return (
    <Stack className={styles.entityLink} onClick={onClick} role={hasClick ? 'button' : undefined}>
      {ContentElement}
      {hasClick && <Icon name="chevron-right" size="small" block className={styles.arrow} />}
    </Stack>
  );
};
